// assets/product-custom-property.js
import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { birthstoneRequired, selectedItemCount, itemCount, conditionalItemCount } from './personalisation-rules.js';

/**
 * @typedef {object} ProductCustomPropertyRefs
 * @property {HTMLInputElement | HTMLTextAreaElement} [textInput] - The text/textarea input, when present.
 * @property {HTMLSelectElement} [selectInput] - The dropdown input, when present.
 * @property {HTMLInputElement[]} [radios] - The radio inputs, when present.
 * @property {HTMLElement} [characterCount] - The character count element.
 * @property {HTMLElement} [errorMessage] - The inline error message element.
 */

/**
 * A custom element that manages a single product personalization field: character counting,
 * and inline (non-native-tooltip) required / min-length validation that works the same way
 * whether the field is submitted via the main Add to Cart button, the accelerated checkout
 * button, or the Quick Add "Choose options" modal - all three share the same underlying
 * <form>, and this component listens to the browser's own constraint validation instead of
 * re-implementing it, so it never gets out of sync with what will actually block submission.
 *
 * @extends Component<ProductCustomPropertyRefs>
 */
class ProductCustomProperty extends Component {
  #abort;
  #values = [];
  #optionNames = [];

  connectedCallback() {
    super.connectedCallback();
    if (!this.dataset.productId) return;
    this.#abort = new AbortController();
    this.#values = JSON.parse(this.dataset.selectedOptions || '[]');
    this.#optionNames = JSON.parse(this.dataset.optionNames || '[]');
    const signal = this.#abort.signal;
    const scope = this.closest('product-card, dialog, .shopify-section');
    scope?.addEventListener('change', (event) => {
      if (!event.target.closest('variant-picker')) return;
      this.#readSelectedOptions();
      this.#syncDesign();
      this.validate(false);
    }, { signal });
    scope?.addEventListener(StandardEvents.productSelect, async (event) => {
      if (event.target.closest('product-card') && !event.target.closest('product-card').contains(this)) return;
      try {
        const { detail } = await event.promise;
        if (String(detail?.productId || detail?.newProduct?.id) !== this.dataset.productId) return;
        this.#readSelectedOptions();
        this.#syncDesign();
        this.validate();
      } catch (error) {
        if (error?.name !== 'AbortError') console.warn('Personalisation could not refresh.', error);
      }
    }, { signal });
    this.#syncDesign();
    this.addEventListener('input', () => this.validate(false), { signal });
    this.addEventListener('change', () => this.validate(false), { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    super.disconnectedCallback();
  }

  #readSelectedOptions() {
    const scope = this.closest('product-card, dialog, .shopify-section');
    const picker = Array.from(scope?.querySelectorAll('variant-picker') || []).find(
      (element) => element.dataset.productId === this.dataset.productId
    );
    if (!picker) return;
    const selects = Array.from(picker.querySelectorAll('select'));
    const values = selects.length
      ? selects.map((select) => select.value)
      : Array.from(picker.querySelectorAll('fieldset')).map((fieldset) => fieldset.querySelector('input:checked')?.value || '');
    if (values.length) this.#values = values;
  }

  #syncDesign() {
    const field = this.refs.selectInput || this.refs.textInput;
    if (!field) return;
    let needed;
    if (this.dataset.birthstoneConditional === 'true') needed = birthstoneRequired(this.#values);
    else if (this.dataset.conditionMode) needed = conditionalItemCount(this.dataset.conditionMode, this.#values) > 0;
    else return;
    field.disabled = !needed;
    field.required = needed;
    field.setAttribute('aria-required', String(needed));
    this.dataset.required = String(needed);
    const wrapper = this.closest('.spacing-style');
    if (wrapper) wrapper.hidden = !needed;
    const optional = wrapper?.querySelector('.__optional-tag');
    if (optional) optional.hidden = needed;
    if (!needed) {
      field.value = '';
      this.#clearError();
    }
  }

  validate(showError = true) {
    this.#readSelectedOptions();
    this.#syncDesign();
    const field = this.refs.textInput || this.refs.selectInput;
    if (!field || field.disabled) return true;
    let message = '';
    if (field.required && field.type !== 'file' && !field.value.trim()) {
      message = this.dataset.errorRequired || 'This field is required.';
    } else if (field.value.trim() && (this.dataset.countKind || /discs|disc-stones/.test(this.dataset.conditionMode))) {
      const count = this.dataset.fixedCount ? Number(this.dataset.fixedCount) : /^(discs|disc-stones)$/.test(this.dataset.conditionMode)
        ? conditionalItemCount(this.dataset.conditionMode, this.#values)
        : selectedItemCount(this.dataset.countKind, this.#optionNames, this.#values);
      if (count !== null && itemCount(field.value) !== count) {
        message = (this.dataset.countError || '').replace('[count]', String(count));
      }
    }
    field.setCustomValidity(message);
    if (message && showError) this.#showError(field);
    if (!message) {
      this.refs.errorMessage?.setAttribute('hidden', '');
      field.removeAttribute('aria-invalid');
    }
    return field.validity.valid;
  }

  handleInput() {
    this.#updateCharacterCount();
    this.#clearError();
    this.validate(false);
  }

  handleBlur() {
    this.validate();
  }

  handleChange(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target instanceof HTMLSelectElement) {
      this.#validateField(target);
    } else if (target instanceof HTMLInputElement && target.type === 'radio') {
      // A radio group is valid as soon as any one option is checked.
      this.#clearError();
    }
  }

  /**
   * Fires when the browser's native constraint validation rejects this field (on submit
   * attempt). We prevent the native tooltip and show our own inline, theme-styled message
   * instead, so the experience is consistent across Add to Cart, Buy it now, and Quick Add.
   * @param {Event} event
   */
  handleInvalid(event) {
    event.preventDefault();
    const target = /** @type {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement} */ (event.target);
    this.#showError(target);
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | undefined} field
   */
  #validateField(field) {
    if (!field) return;

    if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
      // Treat whitespace-only input as empty for required fields - the browser's native
      // `required` check only rejects a truly empty value.
      if (this.dataset.required === 'true' && field.value.trim() === '') {
        field.setCustomValidity(this.dataset.errorRequired || 'This field is required.');
      } else {
        field.setCustomValidity('');
      }
    }

    if (!field.checkValidity()) {
      this.#showError(field);
    } else {
      this.#clearError();
    }
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement} field
   */
  #showError(field) {
    const { errorMessage } = this.refs;
    if (!errorMessage) return;

    let message = field.validity.customError ? field.validationMessage : this.dataset.errorRequired || 'This field is required.';

    if (field.validity && field.validity.tooShort && this.dataset.errorMin) {
      message = this.dataset.errorMin;
    } else if (field.validationMessage && !field.validity.valueMissing && !field.validity.customError) {
      // Fall back to the browser's own message for constraints we don't customize
      // (e.g. maxlength overflow pasted in programmatically), so nothing is ever silently unclear.
      message = field.validationMessage;
    }

    errorMessage.textContent = message;
    errorMessage.hidden = false;
    field.setAttribute('aria-invalid', 'true');
  }

  #clearError() {
    const { errorMessage, textInput, selectInput, radios } = this.refs;
    if (errorMessage) {
      errorMessage.hidden = true;
      errorMessage.textContent = '';
    }
    if (textInput) {
      textInput.setCustomValidity('');
      textInput.removeAttribute('aria-invalid');
    }
    if (selectInput) {
      selectInput.setCustomValidity('');
      selectInput.removeAttribute('aria-invalid');
    }
    if (radios) {
      radios.forEach((radio) => radio.removeAttribute('aria-invalid'));
    }
  }

  #updateCharacterCount() {
    const { characterCount, textInput } = this.refs;
    if (!characterCount || !textInput) return;

    const currentLength = textInput.value.length;
    const maxLength = /** @type {HTMLInputElement | HTMLTextAreaElement} */ (textInput).maxLength;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    characterCount.textContent = updatedText;
  }
}

customElements.define('product-custom-property-component', ProductCustomProperty);
